import { useState } from "react";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (!r.ok) toast.error(r.error);
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-background text-foreground">
      <div className="hidden lg:flex relative flex-col justify-between p-10 border-r border-border overflow-hidden bg-[hsl(var(--brand-blue))]">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(213_94%_35%)] via-[hsl(213_94%_28%)] to-[hsl(218_65%_10%)]" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-white/10 backdrop-blur border border-white/20 grid place-items-center">
              <ShieldCheck className="h-5 w-5 text-[hsl(var(--brand-yellow))]" />
            </div>
            <div>
              <div className="tappav-wordmark text-2xl leading-none text-white">
                Tapp<span className="tappav-av">AV</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/70 mt-1.5">Ops Command Center</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-4">
          <h1 className="font-display text-4xl font-bold tracking-tight text-white leading-[1.1]">
            Run the whole business<br />
            <span className="text-[hsl(var(--brand-yellow))]">from one screen.</span>
          </h1>
          <p className="text-sm text-white/75 max-w-md">
            Every project, task, approval, procurement and payment — tracked, owned and never dropped.
            Built specifically for premium AV & home cinema operations.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 pt-4 text-xs uppercase tracking-[0.2em] text-white/60">
            <span>Projects</span><span>·</span>
            <span>Procurement</span><span>·</span>
            <span>Installation</span><span>·</span>
            <span>Approvals</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <Card className="w-full max-w-md bg-card border-border p-8" data-testid="login-card">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Sign in</div>
            <h2 className="font-display text-2xl font-bold">
              Welcome to <span className="text-[hsl(var(--brand-yellow))]">TappAV Ops</span>
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Enter your credentials to continue.</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username"
                     placeholder="you@tappav.com"
                     className="mt-1 bg-secondary border-border" data-testid="login-email-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                     className="mt-1 bg-secondary border-border" data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
